import {
  useConnectLibrary,
  type ConnectLibraryAnswer,
} from "@/lib/api/connect-library";
import { useConnectQuestionBank } from "@/lib/hooks/use-connect-definition";
import { useConnectQuestionShuffler } from "@/lib/hooks/use-connect-question-shuffler";
import { usePostHogEvent } from "@/lib/posthog";
import type {
  ConnectLevel,
  ConnectQuestion,
} from "@somethings-ui/core/connect-questions";
import { useCallback, useMemo, useReducer, useState } from "react";

// Single MVP cardset. Bump when alternate sets ship.
export const CONNECT_CARDSET_ID = "three_levels_v1";

const ALL_LEVELS_FALSE: Record<ConnectLevel, boolean> = {
  1: false,
  2: false,
  3: false,
};

interface RowsState {
  questions: Record<ConnectLevel, ConnectQuestion>;
  recordingState: Record<ConnectLevel, boolean>;
  localBlobState: Record<ConnectLevel, boolean>;
}

type RowsAction =
  | { type: "shuffle-one"; level: ConnectLevel; next: ConnectQuestion }
  | {
      type: "shuffle-all";
      questions: Record<ConnectLevel, ConnectQuestion>;
    }
  | { type: "set-recording"; level: ConnectLevel; recording: boolean }
  | { type: "set-blob"; level: ConnectLevel; hasBlob: boolean };

function rowsReducer(state: RowsState, action: RowsAction): RowsState {
  switch (action.type) {
    case "shuffle-one":
      return {
        ...state,
        questions: { ...state.questions, [action.level]: action.next },
        localBlobState: { ...state.localBlobState, [action.level]: false },
      };
    case "shuffle-all":
      return {
        ...state,
        questions: action.questions,
        localBlobState: ALL_LEVELS_FALSE,
      };
    case "set-recording":
      return {
        ...state,
        recordingState: {
          ...state.recordingState,
          [action.level]: action.recording,
        },
      };
    case "set-blob":
      return {
        ...state,
        localBlobState: {
          ...state.localBlobState,
          [action.level]: action.hasBlob,
        },
      };
  }
}

export type ShuffleConfirm =
  | { kind: "one"; level: ConnectLevel }
  | { kind: "all" }
  | null;

export interface ConnectRows {
  questions: Record<ConnectLevel, ConnectQuestion>;
  libraryByQuestionId: Record<string, ConnectLibraryAnswer>;
  recordingState: Record<ConnectLevel, boolean>;
  isAnyRowRecording: boolean;
  canSend: boolean;
  onShuffleOne: (level: ConnectLevel) => void;
  onShuffleAll: () => void;
  onRecordingStateChange: (level: ConnectLevel) => (recording: boolean) => void;
  onLocalBlobChange: (level: ConnectLevel) => (hasBlob: boolean) => void;
  shuffleConfirm: ShuffleConfirm;
  confirmShuffle: () => void;
  dismissShuffle: () => void;
}

interface UseConnectRowsOptions {
  mentorshipId: string;
}

export function useConnectRows({
  mentorshipId,
}: UseConnectRowsOptions): ConnectRows {
  const bank = useConnectQuestionBank();
  const shuffler = useConnectQuestionShuffler(bank);
  const { data: libraryData } = useConnectLibrary();
  const { dispatchEvent } = usePostHogEvent("activity.mentor");

  const [state, dispatch] = useReducer(rowsReducer, undefined, () => ({
    questions: {
      1: shuffler.initial.level1,
      2: shuffler.initial.level2,
      3: shuffler.initial.level3,
    },
    recordingState: ALL_LEVELS_FALSE,
    localBlobState: ALL_LEVELS_FALSE,
  }));

  const [shuffleConfirm, setShuffleConfirm] = useState<ShuffleConfirm>(null);

  const { questions, recordingState, localBlobState } = state;

  const libraryByQuestionId = useMemo<
    Record<string, ConnectLibraryAnswer>
  >(() => {
    const map: Record<string, ConnectLibraryAnswer> = {};
    for (const row of libraryData?.library ?? []) {
      map[row.questionId] = row;
    }
    return map;
  }, [libraryData]);

  const isAnyRowRecording =
    recordingState[1] || recordingState[2] || recordingState[3];

  const isRowDirty = useCallback(
    (level: ConnectLevel) =>
      localBlobState[level] || !!libraryByQuestionId[questions[level].id],
    [localBlobState, libraryByQuestionId, questions],
  );

  const performShuffleOne = useCallback(
    (level: ConnectLevel) => {
      const onScreenIds = [
        questions[1].id,
        questions[2].id,
        questions[3].id,
      ];
      const oldQuestion = questions[level];
      const next = shuffler.shuffleOne(
        level,
        onScreenIds.filter((id) => id !== oldQuestion.id),
      );
      dispatch({ type: "shuffle-one", level, next });
      dispatchEvent("mentor_chat_connect_shuffle_question", {
        mentorshipId,
        cardsetId: CONNECT_CARDSET_ID,
        questionIndex: level - 1,
        level,
        oldQuestionId: oldQuestion.id,
        newQuestionId: next.id,
        oldHadRecording: !!libraryByQuestionId[oldQuestion.id],
        newHadRecording: !!libraryByQuestionId[next.id],
      });
    },
    [shuffler, questions, dispatchEvent, mentorshipId, libraryByQuestionId],
  );

  const performShuffleAll = useCallback(() => {
    const oldQuestionIds = [
      questions[1].id,
      questions[2].id,
      questions[3].id,
    ];
    const next1 = shuffler.shuffleOne(1, []);
    const next2 = shuffler.shuffleOne(2, [next1.id]);
    const next3 = shuffler.shuffleOne(3, [next1.id, next2.id]);
    const nextQuestions = { 1: next1, 2: next2, 3: next3 } as const;
    dispatch({ type: "shuffle-all", questions: nextQuestions });
    const newQuestionIds = [next1.id, next2.id, next3.id];
    const rowsWithRecordingsAfter = newQuestionIds.reduce(
      (count, id) => count + (libraryByQuestionId[id] ? 1 : 0),
      0,
    );
    dispatchEvent("mentor_chat_connect_shuffle_all", {
      mentorshipId,
      cardsetId: CONNECT_CARDSET_ID,
      oldQuestionIds,
      newQuestionIds,
      rowsWithRecordingsAfter,
    });
  }, [shuffler, questions, dispatchEvent, mentorshipId, libraryByQuestionId]);

  const onShuffleOne = useCallback(
    (level: ConnectLevel) => {
      if (isRowDirty(level)) {
        setShuffleConfirm({ kind: "one", level });
        return;
      }
      performShuffleOne(level);
    },
    [isRowDirty, performShuffleOne],
  );

  const onShuffleAll = useCallback(() => {
    const anyDirty = ([1, 2, 3] as const).some((l) => isRowDirty(l));
    if (anyDirty) {
      setShuffleConfirm({ kind: "all" });
      return;
    }
    performShuffleAll();
  }, [isRowDirty, performShuffleAll]);

  const onRecordingStateChange = useCallback(
    (level: ConnectLevel) => (recording: boolean) =>
      dispatch({ type: "set-recording", level, recording }),
    [],
  );

  const onLocalBlobChange = useCallback(
    (level: ConnectLevel) => (hasBlob: boolean) =>
      dispatch({ type: "set-blob", level, hasBlob }),
    [],
  );

  // Mirrors BE buildConnectConfig: 409s without a library row per question.
  const canSend = useMemo(() => {
    if (isAnyRowRecording) return false;
    return ([1, 2, 3] as const).every(
      (level) => !!libraryByQuestionId[questions[level].id],
    );
  }, [isAnyRowRecording, libraryByQuestionId, questions]);

  const confirmShuffle = useCallback(() => {
    if (shuffleConfirm?.kind === "one") {
      performShuffleOne(shuffleConfirm.level);
    } else if (shuffleConfirm?.kind === "all") {
      performShuffleAll();
    }
    setShuffleConfirm(null);
  }, [shuffleConfirm, performShuffleOne, performShuffleAll]);

  const dismissShuffle = useCallback(() => setShuffleConfirm(null), []);

  return {
    questions,
    libraryByQuestionId,
    recordingState,
    isAnyRowRecording,
    canSend,
    onShuffleOne,
    onShuffleAll,
    onRecordingStateChange,
    onLocalBlobChange,
    shuffleConfirm,
    confirmShuffle,
    dismissShuffle,
  };
}
